import classNames from 'classnames';
import styles from './ShoppingCart.module.scss';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CartIcon from 'ui/icons/icon-basket.svg';
import CloseIcon from 'ui/icons/icon_close_cross.svg';
import { Icon, Button, ShoppingCartItem } from 'ui/components';
import {
	selectCartProducts,
	removeFromCart,
	decrementCartProductQuantity,
	toggleCartVisiblity,
	addToCart,
} from 'application/adapters/store/slices/cartSlice';
import { RootState } from 'application/adapters/store/store';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { getProducts } from 'application/adapters/store/slices/productsSlice';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';

export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export interface ShoppingCartProps {
	className?: string;
}

export const ShoppingCart: React.FC<ShoppingCartProps> = ({ className }) => {
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		dispatch(getProducts());
	}, [dispatch]);

	const cartProducts = useSelector(selectCartProducts);
	const totalSum = useSelector((state: RootState) => state.cart.totalSum);
	const showCart = useSelector((state: RootState) => state.cart.showCart);

	const dictionary = useContext(DictionaryContext);

	const handleCartVisibility = (show: boolean) => {
		dispatch(toggleCartVisiblity(show));
	};
	const handleRemoval = (cartProduct: Models.CartProduct) => {
		dispatch(removeFromCart(cartProduct));
	};
	const addProduct = (cartProduct: Models.CartProduct) => {
		dispatch(addToCart(cartProduct));
	};
	const handleDecrement = (cartProduct: Models.CartProduct) => {
		dispatch(decrementCartProductQuantity(cartProduct));
	};

	return (
		<div className={classNames(styles.ShoppingCart, className)}>
			<button
				className={classNames(styles.ShoppingCart_button, cartProducts.length > 0 && styles.ShoppingCart_button___hasProduct)}
				onClick={() => handleCartVisibility(true)}
				aria-label={dictionary.getValue('ShoppingCart.OpenCart', null, 'Open shopping cart')}
			>
				<Icon size="md">
					<CartIcon />
				</Icon>
			</button>

			<div className={classNames(styles.ShoppingCart_window, showCart && styles.ShoppingCart_window___visible)}>
				<div className={styles.ShoppingCart_header}>
					<p className={styles.ShoppingCart_heading}>{dictionary.getValue('ShoppingCart', null, 'Shopping cart')}</p>
					<button
						className={styles.ShoppingCart_closeBtn}
						onClick={() => handleCartVisibility(false)}
						aria-label={dictionary.getValue('Actions.Close', null, 'Close')}
					>
						<Icon size="md">
							<CloseIcon />
						</Icon>
					</button>
				</div>
				<div className={styles.ShoppingCart_products}>
					{cartProducts.map((cartProduct, index) => {
						return (
							<ShoppingCartItem
								key={index}
								cartProduct={cartProduct}
								removeProduct={(cartProduct) => handleRemoval(cartProduct)}
								addProduct={(cartProduct) => addProduct(cartProduct)}
								decrementProduct={(cartProduct) => handleDecrement(cartProduct)}
							/>
						);
					})}
				</div>

				<div className={styles.ShoppingCart_checkout}>
					{cartProducts?.length > 0 && (
						<>
							<div className={styles.ShoppingCart_checkoutTotal}>
								<div className={styles.ShoppingCart_checkoutTotalRow}>
									<p>{dictionary.getValue('Price.Total', null, 'Price total')}:</p>
									<p>
										{dictionary.getValue('Price.Currency', null, 'DKK')} {totalSum}
									</p>
								</div>
							</div>
							<Button style="primary" className={styles.ShoppingCart_checkoutButton}>
								{dictionary.getValue('Checkout.GoToCheckout', null, 'Go to checkout')}
							</Button>
						</>
					)}
					{cartProducts?.length == 0 && (
						<p>{dictionary.getValue('ShoppingCart.MissingProducts', null, "You don't have any products in the shopping cart")} </p>
					)}
				</div>
			</div>
			<div
				className={classNames(styles.ShoppingCart_overlay, showCart && styles.ShoppingCart_overlay___visible)}
				onClick={() => handleCartVisibility(false)}
			></div>
		</div>
	);
};
